import { QBox, QInput, QSelectItem, QFlex, QDataTable, DataProvider, Selecting } from '@qualio/ui-components';
import React, { ReactElement, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Group, User, UserTableData } from '../../../types';
import { createTableData } from '../../../utils/users.utils';
import { buildColumns } from './utils';
import { MultiSelect } from '../../../components/MultiSelect';
import { formatGroupsToSelectOptions } from '../../../utils/groups.utils';

type StateSetterFunction<T> = React.Dispatch<React.SetStateAction<T>>;

type UserListProps = {
  users: User[];
  selectedUserIds: Set<number>;
  setSelectedUserIds: React.Dispatch<React.SetStateAction<Set<number>>>;
  groups: Group[];
  selectedGroups: QSelectItem[];
  setSelectedGroups: StateSetterFunction<QSelectItem[]>;
  handleUserAction: (actionItemId: string, userRow: UserTableData) => void;
  filterByGroup: (groupName?: string) => void;
  showProfileFields?: boolean;
};

export const UserList = ({
  users,
  selectedUserIds,
  setSelectedUserIds,
  groups,
  selectedGroups,
  setSelectedGroups,
  handleUserAction,
  filterByGroup,
  showProfileFields = false,
}: UserListProps): ReactElement => {
  const { t } = useTranslation();
  const groupMenuItems = formatGroupsToSelectOptions(groups);
  const [searchQuery, setSearchQuery] = useState('');
  const filteredUsers = useMemo(() => {
    if (!searchQuery) return users;
    return users.filter(
      (user) =>
        user.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [users, searchQuery]);
  const data = useMemo(
    () => createTableData(filteredUsers, handleUserAction, filterByGroup, selectedUserIds, setSelectedUserIds),
    [filteredUsers, selectedUserIds],
  );

  const columns = useMemo(() => {
    return buildColumns(handleUserAction, t, showProfileFields);
  }, [handleUserAction, t, showProfileFields]);

  return (
    <QBox>
      <QFlex gap="8px" mb="1em">
        <MultiSelect
          onChange={(multiSelectedGroups) => setSelectedGroups(multiSelectedGroups as QSelectItem<string, string>[])}
          value={selectedGroups.map((group) => group.value)}
          options={groupMenuItems}
          fixedWidth="250px"
          placeholder={t('users.filterByGroup')}
        />
        <QInput
          placeholder={t('users.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          data-cy="search-users-input"
        />
      </QFlex>
      <Selecting.DefaultSelectionProvider
        onChange={(selectedUserIdStrings) => setSelectedUserIds(new Set(selectedUserIdStrings.map((id) => Number(id))))}
      >
        <DataProvider.Fixed data={data}>
          <QDataTable columns={columns} enableSorting getRowId={({ id }) => id.toString()} />
        </DataProvider.Fixed>
      </Selecting.DefaultSelectionProvider>
    </QBox>
  );
};
